import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { projectAPI } from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';
import { formatMarathiNumber, toMarathiDigits } from '../../utils/marathi';
import './ProjectDetailPage.css';

const CATEGORY_META = {
  road: { label: 'रस्ता', icon: 'road', color: '#2f80ed', bg: '#eaf3ff' },
  water: { label: 'पाणीपुरवठा', icon: 'drop', color: '#23a8e0', bg: '#e8f7ff' },
  water_supply: { label: 'पाणीपुरवठा', icon: 'drop', color: '#23a8e0', bg: '#e8f7ff' },
  drainage: { label: 'सांडपाणी', icon: 'waves', color: '#8b5cf6', bg: '#f1edff' },
  building: { label: 'इमारत', icon: 'building', color: '#9b51e0', bg: '#f3eafe' },
  electrification: { label: 'विद्युतीकरण', icon: 'bolt', color: '#f59e0b', bg: '#fff4dc' },
  electricity: { label: 'विद्युतीकरण', icon: 'bolt', color: '#f59e0b', bg: '#fff4dc' },
  sanitation: { label: 'स्वच्छता', icon: 'spark', color: '#10b981', bg: '#e8f8ef' },
  education: { label: 'शिक्षण', icon: 'book', color: '#2563eb', bg: '#edf4ff' },
  health: { label: 'आरोग्य', icon: 'health', color: '#ef476f', bg: '#fff0f4' },
  agriculture: { label: 'कृषी', icon: 'leaf', color: '#22a447', bg: '#eaf8ef' },
  digital: { label: 'डिजिटल', icon: 'grid', color: '#0ea5e9', bg: '#e9f7ff' },
  other: { label: 'इतर', icon: 'dot', color: '#64748b', bg: '#eef2f6' },
};

const STATUS_META = {
  proposed: { label: 'नवीन', color: '#f59e0b', bg: '#fff4dc' },
  approved: { label: 'मंजूर', color: '#2f80ed', bg: '#eaf3ff' },
  in_progress: { label: 'काम सुरू', color: '#2563eb', bg: '#edf4ff' },
  completed: { label: 'काम पूर्ण', color: '#16a34a', bg: '#e8f8ef' },
  on_hold: { label: 'स्थगित', color: '#8b5cf6', bg: '#f1edff' },
  cancelled: { label: 'रद्द', color: '#e11d48', bg: '#fff0f4' },
};

const formatMoney = value => `₹ ${formatMarathiNumber(value || 0)}`;
const formatDate = value => value ? toMarathiDigits(new Date(value).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' })) : '—';
const getCategory = category => CATEGORY_META[category] || CATEGORY_META.other;
const getStatus = status => STATUS_META[status] || STATUS_META.proposed;

const normalizeProjectDocuments = value => {
  let list = value;
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list);
    } catch {
      list = [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .map(item => {
      if (typeof item === 'string') {
        const trimmed = item.trim();
        return trimmed ? { title: trimmed, meta: '', url: trimmed.startsWith('http') ? trimmed : '' } : null;
      }
      if (!item || typeof item !== 'object') return null;
      const url = String(item.url || item.file_url || item.document_url || item.link || '').trim();
      const title = String(item.title || item.name || item.label || 'प्रकल्प दस्तऐवज').trim();
      const meta = String(item.meta || item.description || item.type || '').trim();
      return { title, meta, url };
    })
    .filter(item => item && (item.title || item.url));
};

const normalizeGallery = (galleryValue, docList = []) => {
  let list = galleryValue;
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list);
    } catch {
      list = list.split('\n').map(l => l.trim()).filter(Boolean).map(l => {
        const [url, caption] = l.split('|').map(s => s.trim());
        return { url, caption: caption || '' };
      });
    }
  }
  const result = Array.isArray(list)
    ? list.map(item => ({
        url: String(item?.url || item?.image_url || '').trim(),
        caption: String(item?.caption || item?.title || '').trim(),
      })).filter(item => item.url)
    : [];

  if (Array.isArray(docList)) {
    const existingUrls = new Set(result.map(r => r.url));
    docList.forEach(doc => {
      const isImg = /\.(png|jpe?g|webp|gif|svg)/i.test(doc.url) || /PNG|JPG|JPEG|WEBP|IMAGE/i.test(doc.meta);
      if (isImg && doc.url && !existingUrls.has(doc.url)) {
        result.push({
          url: doc.url,
          caption: doc.title || 'कामाचे छायाचित्र',
        });
        existingUrls.add(doc.url);
      }
    });
  }

  return result;
};

const buildFallbackUpdates = project => {
  const items = [];
  if (project.start_date) {
    items.push({ title: 'काम सुरू झाले', date: project.start_date, description: 'प्रकल्पाचे प्रारंभिक काम सुरू झाले.' });
  }
  if (Number(project.progress_percentage || 0) >= 25) {
    items.push({
      title: 'मधली प्रगती',
      date: project.expected_end_date || project.start_date,
      description: `${toMarathiDigits(project.progress_percentage)}% प्रगती नोंदवली आहे.`,
    });
  }
  if (project.actual_end_date || project.status === 'completed') {
    items.push({
      title: 'काम पूर्ण',
      date: project.actual_end_date || project.expected_end_date,
      description: 'प्रकल्पाचे मुख्य काम पूर्ण झाले.',
    });
  }
  return items;
};

function Icon({ name }) {
  const common = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  const icons = {
    road: <><path d="M10 3 8 21" /><path d="M14 3l2 18" /><path d="M12 8h.01" /><path d="M12 16h.01" /></>,
    drop: <><path d="M12 2s6 7 6 11a6 6 0 1 1-12 0c0-4 6-11 6-11Z" /></>,
    waves: <><path d="M2 12c2-2 4-2 6 0s4 2 6 0 4-2 6 0 4 2 6 0" transform="translate(-1 0)" /><path d="M2 17c2-2 4-2 6 0s4 2 6 0 4-2 6 0 4 2 6 0" transform="translate(-1 0)" /></>,
    building: <><path d="M4 22V6l8-4 8 4v16" /><path d="M9 22v-4h6v4" /><path d="M8 10h.01" /><path d="M16 10h.01" /><path d="M8 14h.01" /><path d="M16 14h.01" /></>,
    bolt: <><path d="m13 2-7 12h6l-1 8 7-12h-6z" /></>,
    spark: <><path d="M12 3v4" /><path d="M12 17v4" /><path d="M4.93 4.93l2.83 2.83" /><path d="M16.24 16.24l2.83 2.83" /><path d="M3 12h4" /><path d="M17 12h4" /><path d="M4.93 19.07l2.83-2.83" /><path d="M16.24 7.76l2.83-2.83" /></>,
    book: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" /></>,
    health: <><path d="M9 12h6" /><path d="M12 9v6" /><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" /></>,
    leaf: <><path d="M11 20A7 7 0 0 1 4 13C4 5 13 3 20 3c0 7-2 16-9 16Z" /><path d="M4 20c4-7 10-9 16-17" /></>,
    grid: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></>,
    dot: <><circle cx="12" cy="12" r="3" /></>,
    phone: <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.12.92.33 1.82.63 2.69a2 2 0 0 1-.45 2.11L8 9.8a16 16 0 0 0 6.2 6.2l1.28-1.28a2 2 0 0 1 2.11-.45c.87.3 1.77.51 2.69.63A2 2 0 0 1 22 16.92z" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    clock: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
    check: <><path d="M20 6 9 17l-5-5" /></>,
    rupee: <><path d="M6 3h12" /><path d="M6 8h12" /><path d="M6 13h7a5 5 0 0 0 0-10" /><path d="m6 13 8 8" /></>,
    file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
  };

  return <svg {...common}>{icons[name] || icons.dot}</svg>;
}

export default function ProjectDetailPage() {
  const { tenantSlug, projectId } = useParams();
  const { tenantData } = useTenant();
  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');

    Promise.all([
      projectAPI.getById(tenantSlug, projectId),
      projectAPI.getAll(tenantSlug).catch(() => ({ data: { projects: [] } })),
    ])
      .then(([detailRes, listRes]) => {
        setProject(detailRes.data.project);
        setAllProjects(listRes.data.projects || []);
      })
      .catch(() => setError('विकास कामाची माहिती लोड करता आली नाही.'))
      .finally(() => setLoading(false));
  }, [tenantSlug, projectId]);

  const relatedProjects = useMemo(() => {
    if (!project) return [];

    const otherProjects = allProjects.filter(item => String(item.id) !== String(project.id));
    const sameCategory = otherProjects.filter(item => item.category === project.category);
    const shortlisted = (sameCategory.length ? sameCategory : otherProjects)
      .slice()
      .sort((a, b) => {
        const progressDiff = Number(b.progress_percentage || 0) - Number(a.progress_percentage || 0);
        if (progressDiff !== 0) return progressDiff;
        return Number(b.estimated_cost || 0) - Number(a.estimated_cost || 0);
      });

    return shortlisted.slice(0, 3);
  }, [allProjects, project]);

  const [activePreview, setActivePreview] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setActivePreview(null);
    };
    if (activePreview) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activePreview]);

  if (loading) return <div className="project-detail-state">विकास कामाची माहिती लोड होत आहे...</div>;
  if (error || !project) return <div className="project-detail-state error">{error || 'माहिती उपलब्ध नाही.'}</div>;

  const category = getCategory(project.category);
  const status = getStatus(project.status);
  const remaining = Math.max(Number(project.estimated_cost || 0) - Number(project.actual_cost || 0), 0);
  const progress = Number(project.progress_percentage || 0);
  const documents = normalizeProjectDocuments(project.documents);
  const updates = Array.isArray(project.progress_updates) && project.progress_updates.length ? project.progress_updates : buildFallbackUpdates(project);
  const gallery = normalizeGallery(project.gallery, documents);
  const headerImage = gallery[0]?.url;
  const contactPhone = project.contractor_contact || tenantData?.tenant?.contact_phone || '';
  const contactEmail = tenantData?.tenant?.contact_email || '';

  const stageItems = [
    { title: 'योजना मंजूर', date: formatDate(project.createdAt || project.start_date), active: true, icon: 'file' },
    { title: 'प्रशासकीय मंजुरी', date: formatDate(project.start_date || project.createdAt), active: ['approved', 'in_progress', 'completed'].includes(project.status), icon: 'check' },
    { title: 'आर्थिक मंजुरी', date: formatDate(project.start_date || project.expected_end_date), active: ['in_progress', 'completed'].includes(project.status), icon: 'rupee' },
    { title: 'काम सुरू', date: formatDate(project.start_date), active: ['in_progress', 'completed'].includes(project.status), icon: category.icon },
    { title: 'काम पूर्ण', date: formatDate(project.actual_end_date || project.expected_end_date), active: project.status === 'completed', icon: 'target' },
  ];

  return (
    <div className="project-detail-page">
      <nav className="project-detail-breadcrumb">
        <Link to={`/gp/${tenantSlug}`}>मुख्यपृष्ठ</Link>
        <span>›</span>
        <Link to={`/gp/${tenantSlug}/projects`}>विकास कामे</Link>
        <span>›</span>
        <strong>{project.title}</strong>
      </nav>

      <div className="project-detail-layout">
        <aside className="project-detail-left">
          <section className="project-side-card">
            <h2>विकास कामे</h2>
            <Link className="active" to={`/gp/${tenantSlug}/projects`}>सर्व कामे</Link>
            <a href="#project-overview">कामाचे तपशील</a>
            <a href="#project-progress">कामाची प्रगती</a>
            <a href="#project-finance">आर्थिक तपशील</a>
            <a href="#project-documents">प्रकल्प दस्तऐवज ({documents.length})</a>
            <a href="#project-gallery">छायाचित्रे</a>
          </section>

          <section className="project-help-card">
            <span><Icon name="phone" /></span>
            <h2>मदत हवी आहे?</h2>
            <p>योजना किंवा विकास कामाबाबत अधिक माहिती हवी असल्यास ग्रामपंचायत कार्यालयाशी संपर्क साधा.</p>
            <Link to={`/gp/${tenantSlug}/contact`}>संपर्क साधा</Link>
          </section>
        </aside>

        <main className="project-detail-main">
          <section className="project-detail-hero">
            <div className="project-detail-hero-copy">
              <div className="project-detail-topline">
                <span className="project-status-badge" style={{ '--badge-color': status.color, '--badge-bg': status.bg }}>{status.label}</span>
                <span>प्रवर्ग: {category.label}</span>
                <span>आर्थिक वर्ष: {project.financial_year || 'चालू वर्ष'}</span>
              </div>
              <h1>{project.title}</h1>
              <p>{project.description || `${category.label} विभागांतर्गत ग्रामपंचायत हद्दीतील विकास कामाची माहिती.`}</p>
              <div className="project-hero-contact">
                <article><Icon name={category.icon} /><div><span>विभाग</span><strong>{category.label}</strong></div></article>
                <article><Icon name="phone" /><div><span>संपर्क</span><strong>{contactPhone || 'कार्यालयाशी संपर्क करा'}</strong></div></article>
                <article><Icon name="mail" /><div><span>ईमेल</span><strong>{contactEmail || '—'}</strong></div></article>
                <article><Icon name="clock" /><div><span>कार्यालयीन वेळ</span><strong>{tenantData?.tenant?.office_hours || 'सकाळी 10.00 ते सायं. 05.00'}</strong></div></article>
              </div>
            </div>

            <div className="project-detail-hero-media">
              <div className="project-detail-hero-visual">
                {headerImage ? (
                  <img src={headerImage} alt={project.title} />
                ) : (
                  <div className="project-hero-placeholder">
                    <Icon name={category.icon} />
                    <strong>{category.label}</strong>
                    <small>{project.location || 'ग्रामपंचायत विकास काम'}</small>
                  </div>
                )}
              </div>

              <div className="project-hero-summary-card">
                <div className="project-hero-summary-top">
                  <span className="project-hero-summary-icon" style={{ '--hero-accent': category.color, '--hero-soft': category.bg }}>
                    <Icon name={category.icon} />
                  </span>
                  <div>
                    <strong>{category.label}</strong>
                    <small>{project.location || 'देऊळगाव गाडा'}</small>
                  </div>
                </div>
                <div className="project-hero-summary-grid">
                  <article>
                    <span>प्रगती</span>
                    <strong>{formatMarathiNumber(progress)}%</strong>
                  </article>
                  <article>
                    <span>खर्च</span>
                    <strong>{formatMoney(project.actual_cost)}</strong>
                  </article>
                  <article>
                    <span>मंजूर निधी</span>
                    <strong>{formatMoney(project.estimated_cost)}</strong>
                  </article>
                  <article>
                    <span>कालावधी</span>
                    <strong>{formatDate(project.expected_end_date)}</strong>
                  </article>
                </div>
              </div>
            </div>
          </section>

          {/* <div className="project-detail-tabs">
            <a href="#project-overview" className="active">कामाचे तपशील</a>
            <a href="#project-progress">कामाची प्रगती</a>
            <a href="#project-finance">आर्थिक तपशील</a>
            <a href="#project-gallery">छायाचित्रे</a>
            <a href="#project-updates">अद्यतने</a>
          </div> */}

          <section className="project-stage-card" id="project-progress">
            <h2>कामाची सद्यस्थिती</h2>
            <div className="project-stage-strip">
              {stageItems.map(item => (
                <article key={item.title} className={item.active ? 'active' : ''}>
                  <span><Icon name={item.icon} /></span>
                  <strong>{item.title}</strong>
                  <small>{item.date}</small>
                </article>
              ))}
            </div>
          </section>

          <section className="project-summary-grid" id="project-overview">
            <article className="project-overview-card">
              <h2>योजना माहिती</h2>
              <div className="project-overview-list">
                <div><span>कामाचे नाव</span><strong>{project.title}</strong></div>
                <div><span>कामाचे वर्णन</span><strong>{project.description || '—'}</strong></div>
                <div><span>स्थान</span><strong>{project.location || 'देऊळगाव गाडा'}</strong></div>
                <div><span>लांबी / क्षमता</span><strong>{project.project_length || '—'}</strong></div>
                <div><span>लाभार्थी</span><strong>{project.beneficiary || 'ग्रामस्थ'}</strong></div>
                <div><span>एकूण अंदाजित खर्च</span><strong>{formatMoney(project.estimated_cost)}</strong></div>
                <div><span>कामाचा कोड</span><strong>{project.work_code || `PRJ-${project.id}`}</strong></div>
                <div><span>सुरुवात तारीख</span><strong>{formatDate(project.start_date)}</strong></div>
                <div><span>कंत्राटदार नाव</span><strong>{project.contractor_name || 'ग्रामपंचायत / अधिकृत एजन्सी'}</strong></div>
                <div><span>संपर्क क्रमांक</span><strong>{project.contractor_contact || '—'}</strong></div>
              </div>

            </article>

            <article className="project-finance-card" id="project-finance">
              <h2>आर्थिक तपशील</h2>
              <div className="project-money-grid">
                <div><span>एकूण मंजूर निधी</span><strong>{formatMoney(project.estimated_cost)}</strong></div>
                <div className="green"><span>आतापर्यंत खर्च</span><strong>{formatMoney(project.actual_cost)}</strong></div>
                <div className="orange"><span>उर्वरित निधी</span><strong>{formatMoney(remaining)}</strong></div>
                <div className="purple"><span>खर्च टक्केवारी</span><strong>{formatMarathiNumber(progress)}%</strong></div>
              </div>
              <div className="project-progress-bar">
                <div><b style={{ width: `${Math.min(progress, 100)}%` }} /></div>
                <span>{formatMarathiNumber(progress)}%</span>
              </div>
            </article>
          </section>

          {/* Dedicated Prominent Documents Section in Main Content Flow */}
          <section className="project-detail-documents-card" id="project-documents">
            <div className="project-section-title-wrap">
              <div className="project-section-title-left">
                <span className="project-section-icon"><Icon name="file" /></span>
                <h2>प्रकल्प दस्तऐवज (Project Documents)</h2>
              </div>
              <span className="project-doc-count-pill">{documents.length} दस्तऐवज</span>
            </div>
            {documents.length ? (
              <div className="project-docs-list-grid">
                {documents.map((doc, index) => {
                  const isPdf = doc.url?.toLowerCase().includes('.pdf') || doc.meta?.toUpperCase().includes('PDF');
                  const isImage = /\.(png|jpe?g|webp|gif|svg)/i.test(doc.url) || /PNG|JPG|JPEG|WEBP|IMAGE/i.test(doc.meta);
                  return (
                    <article className="project-document-item-box" key={`${doc.title}-${index}`}>
                      {isImage && doc.url ? (
                        <div
                          className="project-doc-thumb-preview"
                          onClick={() => setActivePreview({ type: 'image', url: doc.url, title: doc.title, caption: doc.meta })}
                          title="मोठे करून पहा"
                          role="button"
                          tabIndex={0}
                        >
                          <img src={doc.url} alt={doc.title} />
                          <span className="project-doc-zoom-hint" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                          </span>
                        </div>
                      ) : (
                        <div className={`project-doc-badge ${isPdf ? 'pdf' : isImage ? 'image' : 'file'}`}>
                          <Icon name={isPdf ? 'file' : isImage ? 'spark' : 'file'} />
                          <span>{isPdf ? 'PDF' : isImage ? 'IMG' : 'DOC'}</span>
                        </div>
                      )}

                      <div className="project-doc-meta-info">
                        <h3>{doc.title}</h3>
                        <div className="project-doc-subtags">
                          {doc.meta ? <span className="doc-meta-tag">{doc.meta}</span> : null}
                          <span className="doc-public-tag">सार्वजनिक दस्तऐवज</span>
                        </div>
                      </div>

                      <div className="project-doc-actions-row">
                        {doc.url ? (
                          <>
                            {(isPdf || isImage) && (
                              <button
                                type="button"
                                className="project-doc-preview-action"
                                onClick={() => setActivePreview({ type: isPdf ? 'pdf' : 'image', url: doc.url, title: doc.title, caption: doc.meta })}
                                title="स्क्रीनवर प्रिव्ह्यू पहा"
                              >
                                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                <span>प्रिव्ह्यू</span>
                              </button>
                            )}
                            <a
                              className="project-doc-download-action"
                              href={doc.url}
                              target="_blank"
                              rel="noreferrer"
                              title="उघडा किंवा डाउनलोड करा"
                            >
                              <span>उघडा</span>
                              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                            </a>
                          </>
                        ) : (
                          <span className="project-doc-disabled">उपलब्ध नाही</span>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="project-docs-empty-state">
                <Icon name="file" />
                <p>या कामासाठी प्रशासकीय मंजुरी पत्र, अंदाजपत्रक किंवा निविदा दस्तऐवज अद्याप जोडलेले नाहीत.</p>
                <small>अधिक माहितीसाठी ग्रामपंचायत कार्यालयाशी संपर्क साधा.</small>
              </div>
            )}
          </section>

          <section className="project-gallery-card" id="project-gallery">
            <div className="project-section-title-wrap">
              <div className="project-section-title-left">
                <span className="project-section-icon"><Icon name="spark" /></span>
                <h2>प्रकल्प छायाचित्रे ({gallery.length})</h2>
              </div>
            </div>
            {gallery.length ? (
              <div className="project-gallery-grid">
                {gallery.map((image, index) => (
                  <figure
                    key={`${image.url}-${index}`}
                    className="project-gallery-clickable"
                    onClick={() => setActivePreview({ type: 'image', url: image.url, title: image.caption || `${project.title} - छायाचित्र ${index + 1}` })}
                    title="मोठे करून पहा"
                  >
                    <img src={image.url} alt={image.caption || `${project.title} ${index + 1}`} loading="lazy" />
                    <figcaption>
                      <span>{image.caption || `कामाचे छायाचित्र ${index + 1}`}</span>
                      <span className="gallery-zoom-badge">पहा ↗</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            ) : (
              <div className="project-gallery-empty">छायाचित्रे अद्याप उपलब्ध नाहीत.</div>
            )}
          </section>
        </main>

        <aside className="project-detail-right">
          {relatedProjects.length ? (
            <section className="project-quick-side-card">
              <div className="project-quick-view-head">
                <h2>द्रुत पाहणी</h2>
                <small>संबंधित विकास कामे</small>
              </div>
              <div className="project-quick-side-list">
                {relatedProjects.map(item => {
                  const itemCategory = getCategory(item.category);
                  const itemStatus = getStatus(item.status);
                  const itemProgress = Number(item.progress_percentage || 0);

                  return (
                    <Link
                      key={item.id}
                      className="project-quick-side-item"
                      to={`/gp/${tenantSlug}/projects/${item.id}`}
                      style={{ '--quick-accent': itemCategory.color, '--quick-soft': itemCategory.bg }}
                    >
                      <span className="project-quick-side-icon"><Icon name={itemCategory.icon} /></span>
                      <div>
                        <strong>{item.title}</strong>
                        <small>{item.location || 'देऊळगाव गाडा'}</small>
                        <p>
                          <b>{itemStatus.label}</b>
                          <em>{formatMarathiNumber(itemProgress)}%</em>
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          ) : null}

          <section className="project-doc-card">
            <h2>दस्तऐवज</h2>
            {documents.length ? (
              documents.map((doc, index) => (
                <div key={`${doc.title}-${index}`}>
                  <span><Icon name="file" /></span>
                  <p>
                    <strong>{doc.title}</strong>
                    <small>{doc.meta || 'प्रकल्प दस्तऐवज'}</small>
                  </p>
                  <a href={doc.url} target="_blank" rel="noreferrer">पहा</a>
                </div>
              ))
            ) : (
              <p className="project-updates-empty">या कामासाठी सार्वजनिक दस्तऐवज admin मधून अपलोड केलेले नाहीत. मंजुरी/तांत्रिक तपशीलासाठी ग्रामपंचायत कार्यालयाशी संपर्क साधा.</p>
            )}
          </section>

          <section className="project-updates-card" id="project-updates">
            <h2>अहवाल व अद्यतने</h2>
            {updates.length ? updates.map((update, index) => (
              <article key={`${update.title}-${index}`}>
                <span />
                <div>
                  <strong>{update.title}</strong>
                  <small>दिनांक: {formatDate(update.date)}</small>
                  <p>{update.description}</p>
                </div>
              </article>
            )) : <p className="project-updates-empty">अद्यतने उपलब्ध नाहीत.</p>}
            <Link to={`/gp/${tenantSlug}/projects`}>सर्व अद्यतने पहा</Link>
          </section>
        </aside>
      </div>

      {/* On-screen Preview Lightbox Modal */}
      {activePreview && (
        <div className="project-preview-modal-overlay" onClick={() => setActivePreview(null)}>
          <div className="project-preview-modal-content" onClick={e => e.stopPropagation()}>
            <div className="project-preview-modal-header">
              <div className="project-preview-modal-title">
                <Icon name={activePreview.type === 'pdf' ? 'file' : 'spark'} />
                <h3>{activePreview.title || 'दस्तऐवज प्रिव्ह्यू'}</h3>
                {activePreview.caption ? <span>{activePreview.caption}</span> : null}
              </div>
              <div className="project-preview-modal-actions">
                <a
                  href={activePreview.url}
                  target="_blank"
                  rel="noreferrer"
                  className="project-modal-action-btn"
                  title="नवीन टॅबमध्ये उघडा"
                >
                  नवीन टॅब ↗
                </a>
                <button
                  type="button"
                  className="project-modal-close-btn"
                  onClick={() => setActivePreview(null)}
                  title="बंद करा"
                  aria-label="बंद करा"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="project-preview-modal-body">
              {activePreview.type === 'image' ? (
                <div className="project-preview-image-wrap">
                  <img src={activePreview.url} alt={activePreview.title} />
                </div>
              ) : (
                <div className="project-preview-pdf-wrap">
                  <iframe
                    src={activePreview.url}
                    title={activePreview.title}
                    className="project-preview-pdf-frame"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
